import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  Input,
} from '@roger/ui-components';
import { ClipboardList, Plus, Search } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { PermissionSlug } from '../constants/featureFlags';
import { useStore } from '../store';
import ReportsPermissionAlert from './ReportsPermissionAlert';

const reports = [
  {
    title: 'Census Report',
    description: 'Current patients and episode-level census rows.',
    href: '/reporting/census',
    category: 'Clinical',
    requiredPermission: PermissionSlug.ROGER_EMR_REPORTS_ACCESS,
  },
  {
    title: 'Patient Diagnoses',
    description:
      'Review active patients by diagnosis, payer, and HIPPS details.',
    href: '/reporting/patient-diagnoses',
    category: 'Clinical',
    requiredPermission: PermissionSlug.ROGER_EMR_REPORTS_ACCESS,
  },
  {
    title: 'Schedule Report',
    description: 'Review scheduled visits by date, clinician, and status.',
    href: '/reporting/schedule',
    category: 'Operations',
    requiredPermission: PermissionSlug.ROGER_EMR_REPORTS_ACCESS,
  },
  {
    title: 'Schedule Deviation',
    description: 'Find visits documented outside their planned schedule.',
    href: '/reporting/schedule-deviation',
    category: 'Operations',
    requiredPermission: PermissionSlug.ROGER_EMR_REPORTS_ACCESS,
  },
  {
    title: 'Adoption / Visit Completion',
    description: 'Clinician visit completion and adoption metrics.',
    href: '/reporting/adoption',
    category: 'Documentation',
    requiredPermission: PermissionSlug.REPORTS_ACCESS,
  },
  {
    title: 'Worklog Report',
    description: 'Review task worklog activity by user and date.',
    href: '/reporting/worklog',
    category: 'Operations',
    requiredPermission: PermissionSlug.ROGER_EMR_REPORTS_ACCESS,
  },
  {
    title: 'Wound Status Report',
    description: 'Review wound status, location, and progression by patient.',
    href: '/reporting/wound-status',
    category: 'Clinical',
    requiredPermission: PermissionSlug.ROGER_EMR_REPORTS_ACCESS,
  },
];

const Reporting = () => {
  const [search, setSearch] = useState('');
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );

  const visibleReports = useMemo(
    () => reports.filter(report => hasPermission(report.requiredPermission)),
    [hasPermission]
  );

  const filteredReports = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return visibleReports;
    }

    return visibleReports.filter(report =>
      [report.title, report.description, report.category].some(value =>
        value.toLowerCase().includes(query)
      )
    );
  }, [search, visibleReports]);

  if (visibleReports.length === 0) {
    return <ReportsPermissionAlert />;
  }

  return (
    <div className='space-y-8'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <h1 className='text-2xl font-bold'>Reports</h1>
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <label className='relative block w-72 max-w-full'>
            <span className='sr-only'>Search reports</span>
            <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              type='search'
              inputSize='default'
              className='pl-9'
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder='Search reports...'
            />
          </label>
          <Button disabled title='Custom report builder is not available yet'>
            <Plus className='h-4 w-4' /> New report
          </Button>
        </div>
      </div>

      <section className='space-y-3'>
        <h2 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Standard reports
        </h2>
        {filteredReports.length > 0 ? (
          <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
            {filteredReports.map(report => (
              <Link key={report.href} to={report.href} className='block'>
                <Card className='h-full min-h-40 transition-colors hover:border-primary/60 hover:bg-muted/30'>
                  <CardHeader className='space-y-8'>
                    <div className='flex items-start gap-3'>
                      <ClipboardList className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                      <div className='min-w-0 space-y-1'>
                        <CardTitle className='text-base leading-tight'>
                          {report.title}
                        </CardTitle>
                        <p className='text-sm font-normal leading-snug text-muted-foreground'>
                          {report.description}
                        </p>
                      </div>
                    </div>
                    <Badge variant='outline' className='w-fit'>
                      {report.category}
                    </Badge>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className='rounded-lg border bg-card p-6 text-sm text-muted-foreground'>
            No reports match your search.
          </div>
        )}
      </section>
    </div>
  );
};

export default Reporting;
